/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Factory for creating i18n instances with default or custom configurations.
 * Implements Dependency Inversion Principle by depending on abstractions.
 *
 * */

import { I18nService } from '../services/I18nService'
import { TranslationProvider } from '../providers/TranslationProvider'
import type { Ti18nConfig, Ti18nLocale } from '../types'

export class I18nFactory {
  static createWithProvider(
    translations: Record<Ti18nLocale, Record<string, string>>,
    config?: Ti18nConfig
  ): I18nService {
    const provider = new TranslationProvider(translations)
    return new I18nService(provider, config)
  }

  static createWithOverrides(
    baseTranslations: Record<Ti18nLocale, Record<string, string>>,
    overrides: Partial<Record<Ti18nLocale, Record<string, string>>>,
    config?: Ti18nConfig
  ): I18nService {
    const mergedTranslations = { ...baseTranslations } as Record<
      Ti18nLocale,
      Record<string, string>
    >

    Object.entries(overrides).forEach(([locale, translations]) => {
      if (translations) {
        mergedTranslations[locale as Ti18nLocale] = {
          ...mergedTranslations[locale as Ti18nLocale],
          ...translations,
        }
      }
    })

    return I18nFactory.createWithProvider(mergedTranslations, config)
  }
}
